import RAPIER from '@dimforge/rapier3d-compat';
import { Vector3 } from 'three/webgpu';
import type { GravityField } from '../gravity/GravityField';

/**
 * Rapier runs in f32, so it simulates in a local frame whose origin (f64,
 * universe space) follows the player. `rebase()` shifts everything when the
 * player strays too far. Gravity is ours: Rapier's world gravity is zero and
 * each dynamic body receives the local field every step.
 */
export interface StaticMesh {
  body: RAPIER.RigidBody;
  collider: RAPIER.Collider;
  /** Universe-space anchor the vertices are relative to. */
  anchor: Vector3;
}

export interface DynamicBody {
  body: RAPIER.RigidBody;
  /** Optional per-body hook, e.g. drag. */
  userData?: unknown;
}

const REBASE_DISTANCE = 1000;

const _p = new Vector3();
const _g = new Vector3();

export class PhysicsWorld {
  readonly world: RAPIER.World;
  /** Universe position of Rapier's (0,0,0). */
  readonly origin = new Vector3();
  private readonly statics = new Set<StaticMesh>();
  private readonly dynamics = new Set<DynamicBody>();

  static async create(field: GravityField, origin: Vector3): Promise<PhysicsWorld> {
    await RAPIER.init();
    return new PhysicsWorld(field, origin);
  }

  private constructor(
    readonly field: GravityField,
    origin: Vector3,
  ) {
    this.world = new RAPIER.World({ x: 0, y: 0, z: 0 });
    this.world.timestep = 1 / 60;
    this.origin.copy(origin);
  }

  toLocal(universe: Vector3, out: Vector3): Vector3 {
    return out.subVectors(universe, this.origin);
  }

  toUniverse(local: { x: number; y: number; z: number }, out: Vector3): Vector3 {
    return out.set(local.x + this.origin.x, local.y + this.origin.y, local.z + this.origin.z);
  }

  /** Static triangle mesh. Vertices are relative to `anchor` (universe). */
  addStaticMesh(anchor: Vector3, vertices: Float32Array, indices: Uint32Array): StaticMesh {
    this.toLocal(anchor, _p);
    const body = this.world.createRigidBody(RAPIER.RigidBodyDesc.fixed().setTranslation(_p.x, _p.y, _p.z));
    const desc = RAPIER.ColliderDesc.trimesh(
      vertices,
      indices,
      RAPIER.TriMeshFlags.FIX_INTERNAL_EDGES_TWO_SIDED,
    );
    desc.setFriction(0.8);
    const collider = this.world.createCollider(desc, body);
    const mesh: StaticMesh = { body, collider, anchor: anchor.clone() };
    this.statics.add(mesh);
    return mesh;
  }

  removeStaticMesh(mesh: StaticMesh): void {
    if (!this.statics.delete(mesh)) return;
    this.world.removeRigidBody(mesh.body);
  }

  addDynamic(body: RAPIER.RigidBody, userData?: unknown): DynamicBody {
    body.setGravityScale(0, false);
    const d: DynamicBody = { body, userData };
    this.dynamics.add(d);
    return d;
  }

  removeDynamic(d: DynamicBody): void {
    if (!this.dynamics.delete(d)) return;
    this.world.removeRigidBody(d.body);
  }

  /** Wake every dynamic body, e.g. after the ground under them was rebuilt. */
  wakeAll(): void {
    for (const d of this.dynamics) d.body.wakeUp();
  }

  /** Re-centre the simulation on `center` if it has drifted too far. */
  maybeRebase(center: Vector3): boolean {
    if (center.distanceTo(this.origin) < REBASE_DISTANCE) return false;
    this.rebase(center);
    return true;
  }

  rebase(newOrigin: Vector3): void {
    const dx = this.origin.x - newOrigin.x;
    const dy = this.origin.y - newOrigin.y;
    const dz = this.origin.z - newOrigin.z;
    this.world.forEachRigidBody((body) => {
      const t = body.translation();
      const next = { x: t.x + dx, y: t.y + dy, z: t.z + dz };
      body.setTranslation(next, false);
      if (body.isKinematic()) body.setNextKinematicTranslation(next);
    });
    this.origin.copy(newOrigin);
  }

  step(dt: number): void {
    for (const d of this.dynamics) {
      const b = d.body;
      if (b.isSleeping()) continue;
      this.toUniverse(b.translation(), _p);
      this.field.sample(_p, _g);
      const m = b.mass();
      b.applyImpulse({ x: _g.x * m * dt, y: _g.y * m * dt, z: _g.z * m * dt }, true);
    }
    this.world.timestep = dt;
    this.world.step();
  }
}

export { RAPIER };
